import type { FC } from 'react';

import { useApi } from '@backstage/frontend-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { isLeanixEntity } from '@ipen-portal/plugin-common';
import { Alert, Stack, Typography } from '@mui/material';
import { skipToken, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { tss } from 'tss-react/mui';

import { tdhApiRef } from '../apis';
import { DataRow } from './DataRow';
import { TdhDetailTile } from './TdhDetailTile';

const useStyles = tss.create(({ theme }) => ({
  alert: {
    marginTop: theme.spacing(1),
  },
  stack: {
    marginTop: theme.spacing(1),
  },
}));

export const ComplianceCard: FC = () => {
  const tdhApi = useApi(tdhApiRef);
  const { entity } = useEntity();
  const leanixId = isLeanixEntity(entity)
    ? entity.spec.leanix.aplElementId
    : undefined;
  const factsheetUrl = isLeanixEntity(entity)
    ? entity.spec.leanix.factsheetUrl
    : undefined;
  const { data, error, isLoading } = useQuery(
    tdhApi.getCompliance.queryOptions(
      leanixId ? { aplElementId: leanixId } : skipToken,
    ),
  );
  const { classes } = useStyles();

  const content = data
    ? (
        <Stack className={classes.stack} spacing={2}>
          <Typography gutterBottom variant="h6">
            Classification
          </Typography>
          <Stack spacing={1}>
            <DataRow
              label="Confidentiality:"
              value={data.classification.confidentiality ?? 'Unknown'}
            />
            <DataRow
              label="Integrity:"
              value={data.classification.integrity ?? 'Unknown'}
            />
            <DataRow
              label="Availability:"
              value={data.classification.availability ?? 'Unknown'}
            />
            <DataRow
              label="Recovery Time Objective (RTO):"
              value={data.classification.rto?.toString() ?? '-'}
            />
            <DataRow
              label="Recovery Point Objective (RPO):"
              value={data.classification.rpo?.toString() ?? '-'}
            />
            <DataRow
              label="Level of Assurance:"
              value={data.classification.levelOfAssurance ?? 'Unknown'}
            />
            <DataRow
              label="Security Classification Signed:"
              value={data.classification.securityClassificationSigned}
            />
            <DataRow
              label="Information Security Comments:"
              value={data.classification.informationSecurityComments ?? '-'}
            />
            <DataRow
              label="Security Classification - Workflow Status:"
              value={data.classification.securityRequirementsStatus ?? 'Unknown'}
            />
            <DataRow
              label="Security Classification - Workflow Start Date:"
              value={
                data.classification.securityClassificationWorkflowStartDate
                  ? dayjs(
                      data.classification.securityClassificationWorkflowStartDate,
                    ).format('YYYY-MM-DD')
                  : '-'
              }
            />
          </Stack>
        </Stack>
      )
    : (
        <Alert className={classes.alert} severity="info">
          No data returned for this LeanIX ID
        </Alert>
      );

  return (
    <TdhDetailTile
      error={error}
      factsheetUrl={factsheetUrl}
      icon="mdi:clipboard-check"
      isLoading={isLoading}
      leanixId={leanixId}
      title="Compliance"
    >
      {content}
    </TdhDetailTile>
  );
};
